<template>
  <sticky-header-layout>
    <!-- Header -->
    <template #header>
      <div class="action-container justify-content-end">
        <span>
          <el-button
            :disabled="isSaving"
            class="cancel-button"
            size="mini"
            type="warning"
            icon="el-icon-close"
            @click="handleCancel"
          >{{ $t('button.cancel') }}</el-button>
          <el-button
            :disabled="isLoading"
            :loading="isSaving"
            class="save-button"
            size="mini"
            type="danger"
            icon="el-icon-upload2"
            @click="handleSave"
          >{{ $t('button.save') }}</el-button>
        </span>
      </div>
    </template>
    <!-- Header -->

    <div v-loading="isLoading">
      <!-- Form -->
      <el-form
        ref="form"
        :disabled="isSaving"
        :model="form"
        class="round-border-form pt-3 mt-3"
        label-width="124px"
      >
        <template
          v-for="{ key, ...column } in formOrder"
        >
          <el-form-item
            v-if="key === 'id' && isEditMode"
            :label="$t(`table.${key}`)"
            :key="key"
          >
            <div>{{ form.id }}</div>
          </el-form-item>

          <!-- Input -->
          <el-form-item
            v-else-if="column.isInput"
            :label="$t(`table.${key}`)"
            :key="key"
            :rules="column.rules"
            :prop="key"
          >
            <el-input
              v-model.trim="form[key]"
              v-bind="column.custom"
              clearable
            />
            <img
              v-if="checkImageURL(form[key])"
              :src="form[key]"
              class="table-img"
            >
          </el-form-item>
          <!-- Input -->
        </template>
      </el-form>
      <!-- Form -->
    </div>
  </sticky-header-layout>
</template>

<script>
import { checkImageURL } from '../../../utils/validate'
import { FormRules } from '../../../utils/form'

import MarketplaceService from '../../../api/marketplace'

export default {
  name: 'FeaturedBrandDetail',
  props: {
    id: {
      type: [String, Number],
      default: undefined,
    },
  },
  data() {
    const formRules = {
      requiredStringField: [FormRules.required],
      imageUrl: [FormRules.required, FormRules.imageUrl],
    }
    const formOrder = [
      { key: 'id' },
      { key: 'name', isInput: true, rules: formRules.requiredStringField },
      {
        key: 'logo_image',
        isInput: true,
        rules: formRules.imageUrl,
        custom: { placeholder: 'https://' },
      },
      {
        key: 'featured_image',
        isInput: true,
        rules: formRules.imageUrl,
        custom: { placeholder: 'https://' },
      },
      { key: 'url', isInput: true, rules: formRules.requiredStringField },
    ]
    return {
      isEdited: false,
      isLoading: false,
      isSaving: false,
      formOrder,
      form: {
        name: '',
        logo_image: '',
        featured_image: '',
        url: '',
      },
    }
  },
  computed: {
    isEditMode() {
      return !!this.id
    },
  },
  watch: {
    form: {
      handler() {
        this.isEdited = true
      },
      deep: true,
    },
  },
  mounted() {
    if (this.isEditMode) {
      this.getDetail(this.id)
    }
  },
  beforeRouteLeave(to, from, next) {
    if (this.isSaving) {
      return next(false)
    }
    if (!this.isEdited) {
      return next()
    }

    this.$confirm(this.$t('message.leaveWithUnsavedChanges'), this.$t('common.warning'), {
      confirmButtonText: this.$t('button.leave'),
      cancelButtonText: this.$t('button.cancel'),
      type: 'warning',
    }).then(() => next()).catch(() => next(false))

    return next(false)
  },
  methods: {
    goToListPage() {
      this.$router.push({ path: '/trending/marketplace-brands' })
    },
    handleCancel() {
      this.goToListPage()
    },
    handleSave() {
      this.$refs.form.validate().then(() => {
        if (this.isEditMode) {
          this.editFeaturedBrands(this.form, this.id)
        } else {
          this.addFeaturedBrands(this.form)
        }
      }).catch((error) => {
        console.error(error);
        this.$alert(this.$t('error.pleaseFillInAndCheckErrors'), this.$t('common.oops'), {
          confirmButtonText: this.$t('button.ok'),
        });
      })
    },
    handleSaveSuccess() {
      this.isSaving = false
      this.isEdited = false
      this.goToListPage()
    },
    handleSaveFail(error) {
      this.isSaving = false
      this.$alert(`Error: ${error}`, this.$t('error.somethingWentWrong'), {
        type: 'error',
        confirmButtonText: this.$t('button.ok'),
      })
    },
    async getDetail(id) {
      this.isLoading = true
      try {
        const { data } = await MarketplaceService.getFeaturedBrandDetail(id)
        this.form = data
        this.$nextTick(() => {
          this.isEdited = false
        })
      } catch (error) {
        console.error(error);
        this.$alert(this.$t('error.brandNotFound'), this.$t('common.error'), {
          type: 'error',
          confirmButtonText: this.$t('button.ok'),
        }).then(() => {
          this.goToListPage()
        })
      } finally {
        this.isLoading = false
      }
    },
    async editFeaturedBrands(form) {
      this.isSaving = true
      try {
        await MarketplaceService.editFeaturedBrand(form, this.id)
        this.handleSaveSuccess()
      } catch (error) {
        this.handleSaveFail(error)
      }
    },
    async addFeaturedBrands(form) {
      this.isSaving = true
      try {
        await MarketplaceService.addFeaturedBrand(form)
        this.handleSaveSuccess()
      } catch (error) {
        this.handleSaveFail(error)
      }
    },
    checkImageURL,
  },
}
</script>

<style lang="scss" scoped>
@import "~@/assets/styles/view-base.scss";
@import "~@/assets/styles/table.scss";
@import "~@/assets/styles/form.scss";

.el-form-item {
  padding-bottom: 6px;
}

.table-img {
  width: unset;
}

::v-deep {
  .el-form-item__label {
    + .el-form-item__content {
      > *:not(.el-input):not(.el-textarea):not(.multiselect) {
        margin-top: 4px;
      }
    }
  }
  .el-table .el-table__row td {
    border: none;
    vertical-align: top;
  }
  .el-select__input.is-mini {
    height: auto;
  }
}
</style>
